import { Storage } from 'types/common';
import { SubmittedAsset } from 'types/report';
import { Documentation } from 'types/documents';
import { AssetDevice, MaintenanceConfig } from 'app/pages/ValidationsPage/type';

export type Room = {
  id: number;
  roomId: number;
  roomCustomId?: string;
  roomCustomId2?: string;
  userId: number;
  clientId: number;
  date: string;
  approver: string;
  long: string;
  details: ParentLocation;
  lat: string;
  documentControl: string;
  complianceRepresentative?: string;
  frequency: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  submittedPhotos: SubmittedPhoto[];
  roomLevelIssues: RoomLevelIssues[];
  customAttributes: CustomAttributes[];
  additionalStructures: AdditionalStructures[];
  options: { id: number; type: string; value: string; dropdownValueOptionId: number }[];
};

export const TypePhotoSubmit = {
  OUTSIDE: 'OUTSIDE',
  INTERNAL: 'INTERNAL',
  WALL1: 'WALL1',
  WALL2: 'WALL2'
} as const;

export type TypePhotoSubmit = keyof typeof TypePhotoSubmit;
export type TypeRoomLevelIssues = string;
export type FieldCustomAttributes = 'annual';

export type SubmittedPhoto = {
  id: number;
  submitId: number;
  storageId: number;
  orderIndex?: number;
  type: TypePhotoSubmit;
  assetIssueId: number;
  createdAt: string;
  updatedAt: string;
  storage: Storage;
  url: string;
  originUrl: string;
  photoTags: {
    width: number;
    height: number;
    id: number;
    xAxis: number;
    submittedAssetId: number;
    yAxis: number;
  }[];
};
export type RoomLevelIssuePhotos = {
  id: number;
  roomLevelIssueId: number;
  photoId: number;
  createdAt: string;
  updatedAt: string;
  photo: Storage;
  url: string;
  originURL: string;
};

export type RoomLevelIssues = {
  actionByName: string;
  id: number;
  submitId: number;
  type: TypeRoomLevelIssues;
  description: string;
  validated: boolean;
  photoId: number;
  createdAt: string;
  actionBy: string;
  actionByDate: string;
  actionNeeded: string;
  status: string;
  updatedAt: string;
  room: ParentLocation;
  photo: Storage;
  url: string;
  options: IOptions[];
  archivedAt: Date | null;
  resolutionDate?: string;
  roomLevelIssuePhotos: RoomLevelIssuePhotos[];
  dueDate?: string | null;
  criticalityKey?: string | null;
  criticality?: { key?: string; label?: string; color?: string } | null;
  assignedByUser?: { id?: number; name?: string; email?: string } | null;
  assignedToUser?: { id?: number; name?: string; email?: string } | null;
  reportedByUser?: { id?: number; name?: string; email?: string } | null;
};
export type CustomAttributes = {
  id: number;
  submitId: number;
  field: FieldCustomAttributes;
  description: string;
  photoId: number;
  createdAt: string;
  updatedAt: string;
  photo: Storage;
  url: string;
};
export interface IOptions {
  id: number;
  submittedAssetId: number;
  fieldType?: string;
  type: string;
  value: string;
}
export type OemAttributes = {
  value?: string;
  label: string;
  isDropdown?: boolean;
  dropdownId?: number | null;
  dropdownValue?: number | null;
};
export type AssetIssues = {
  id: number;
  name: string;
  assetCustomId?: string;
  assetCustomId2?: string;
  archivedAt: string;
  submitId: number;
  equipmentId: string;
  condition: string;
  /** @deprecated legacy integer from API; prefer criticalityKey */
  criticality?: number;
  // criticality: number;
  criticalityKey?: string | null;
  hasIssue: boolean;
  validated: boolean;
  make: number;
  photoId: number;
  altText: string;
  location: string;
  model: Model;
  type: Type;
  photos: {
    id?: number;
    url: string;
    storage: Storage;
  }[];
  submittedEntries: SubmittedEntries[];
  submitted: { roomId?: number; long?: string; lat?: string };
  issueReports: IssueReport[];
  documentations: Documentation[];
  assetDocumentations: Documentation[];
  createdAt: string;
  updatedAt: string;
  clientName: string;
  mainLocation: string;
  subLocation1: string;
  subLocation2: string;
  verticalLevel: string;
  room: ParentLocation;
  tags: Tag[];
  options: IOptions[];
  note?: string;
  otherNote?: string;
  lastMaintenance?: string;
  oemAttributes?: OemAttributes[];
  maintenanceEnabled?: boolean; // ← add
  assetDevices?: AssetDevice[]; // ← add
  modelId?: number;
};

export type Tag = {
  id: number;
  submittedAssetId: number;
  submittedPhotoId: number;
  additionalStructurePhotoId: number;
  createdAt: string;
  updatedAt: string;
  submittedPhoto: {
    id: number;
    submitId: number;
    storageId: number;
    originId: number;
    type: string;
    createdAt: string;
    updatedAt: string;
  };
  additionalStructurePhoto: {
    id: number;
    submitId: number;
    storageId: number;
    originId: number;
    type: string;
    createdAt: string;
    updatedAt: string;
    additionalStructure: {
      type: string;
    };
  };
};

export type IssueReport = {
  id: number;
  name: string;
  archivedAt: string;
  type: string;
  url: string;
  submittedAssetId: number;
  submitId?: number;
  equipmentId: string;
  description: string;
  submittedAsset: SubmittedAsset;
  complianceIssue: string;
  actionNeeded: string;
  status: string;
  actionBy: string;
  actionByDate: string;
  dueDate?: string | null;
  criticalityKey?: string | null;
  criticality?: { key?: string; label?: string; color?: string } | null;
  assignedByUser?: { id?: number; name?: string; email?: string } | null;
  assignedToUser?: { id?: number; name?: string; email?: string } | null;
  reportedByUser?: { id?: number; name?: string; email?: string } | null;
  createdAt: string;
  updatedAt: string;
  photo: Storage;
  issueReportPhotos: IssueReport[];
  options: IOptions[];
  resolutionDate?: Date;
};

export type AdditionalStructures = {
  id: number;
  submitId: number;
  type: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  additionalStructurePhotos: AdditionalStructuresPhoto[];
};
export type AdditionalStructuresPhoto = {
  id: number;
  structureId: number;
  photoId: number;
  orderIndex?: number;
  createdAt: string;
  updatedAt: string;
  photo: Storage;
  url: string;
  originURL: string;
  photoTags: {
    width: number;
    height: number;
    id: number;
    xAxis: number;
    submittedAssetId: number;
    yAxis: number;
  }[];
};
export type ParentLocation = {
  id: number;
  name: string;
  parentId: number;
  clientId: number;
  level: number;
  createdAt: string;
  updatedAt: string;
  client: {
    id: number;
    clientName: string;
    clientEmail: string;
    contactPerson: string;
  };
  parent: ParentLocation;
};

export type SubmittedEntries = {
  id: number;
  assetIssueId: number;
  fieldId: number;
  value: string;
  dropdownValueId?: number;
  createdAt: string;
  updatedAt: string;
  dropdownOtherValue?: string;
  dropdownValue?: {
    id: number;
    name: string;
    value: string;
  };
  field: {
    id: number;
    typeId: number;
    name: string;
    unit: string;
    category: string;
    createdAt: string;
    updatedAt: string;
    dropdownValueOptionId?: number;
    dropdownValueOption?: {
      id: number;
      name: string;
      dropdownValues: {
        id: number;
        name: string;
        value: string;
      }[];
    };
  };
};

export type Model = {
  id: number;
  name: string;
  manufacturerId: number;
  manufacturer: Manufacturer;
  oemAttributes?: {
    label: string;
    value?: string;
    isDropdown?: boolean;
    dropdownId?: number | null;
    dropdownValue?: number | null;
  }[];
  typeId: number;
  createdAt: string;
  updatedAt: string;
  maintenanceEnabled?: boolean; // ← add
  maintenanceConfigs?: MaintenanceConfig[]; // ← add
};
export type Manufacturer = {
  createdAt: string;
  deletedAt: string | null;
  id: number;
  name: string;
  updatedAt: string;
};
export type Type = {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
};

export type Document = {
  id: number;
  roomId: number;
  storageId: number;
  createdAt: string;
  updatedAt: string;
  title: string;
  storage: Storage;
};

export type RoomInfo = {
  id?: number;
  name: string;
  isClient?: boolean;
  level: number;
  onClick?: () => void;
}[];
